import React from 'react';
import { Avatar, Flex } from 'antd';
import { PeopleAltIcon } from '@/assets/icons/icons';
import type { NotificationGroup, ShipmentMessage } from '@/apis/shipment';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';

interface NotificationListProps {
  groups: NotificationGroup[];
  onNotificationClick: (message: ShipmentMessage) => void;
}

export const isMentionMessage = (message: ShipmentMessage): boolean =>
  message.type === 'mention';

const getMessageContext = (message: ShipmentMessage): string => {
  const typeLabel = isMentionMessage(message) ? '@mention' : 'Reply';
  const referenceNumber = message.shipment.reference_number;
  return `${typeLabel} in ${referenceNumber}`;
};

const getPlainContent = (content?: string): string => {
  if (!content) return '';

  return content
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
};

const renderMessageContent = (content?: string): React.ReactNode => {
  const text = getPlainContent(content);
  if (!text) return null;

  return text.split(/(@[A-Za-z0-9_.-]+)/g).map((part, index) =>
    part.startsWith('@') ? (
      <span className='notification-mention' key={`${part}-${index}`}>
        {part}
      </span>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
};

const NotificationList = ({
  groups,
  onNotificationClick,
}: NotificationListProps) => {
  const { formatDateTime } = useUserDateTimeFormat();

  return (
    <div className='notification-list-scroll'>
      {groups.map((group) => (
        <div key={group.date}>
          <div className='daywise-notification'>
            <span>{group.date}</span>
          </div>
          <div className='notification-day-list'>
            {group.messages.map((message) => (
              <button
                type='button'
                className={`single-notification ${
                  !message.is_read ? 'unread' : ''
                }`}
                key={message.message_id}
                data-testid={`notification-${message.message_id}`}
                onClick={() => onNotificationClick(message)}
              >
                <div className='notification-avatar-wrap'>
                  {!message.is_read && (
                    <div className='unread-dot inner-notification'></div>
                  )}
                  <Avatar
                    className='custom-avatar'
                    style={{
                      backgroundColor: message.user.badge_color || '#afafaf',
                    }}
                  >
                    {message.user.initials}
                  </Avatar>
                </div>
                <div className='notification-content'>
                  <p
                    className='notification-context'
                    title={getMessageContext(message)}
                  >
                    {getMessageContext(message)}
                  </p>
                  <Flex
                    gap={8}
                    align='center'
                    justify='space-between'
                    className='notification-meta'
                  >
                    <p className='notification-author'>{message.user.name}</p>
                    <Flex align='center' gap={4}>
                      <PeopleAltIcon size={16} color='#A7A7A7' />
                      <p className='notification-time'>
                        {formatDateTime(message.timestamp, {
                          includeTime: false,
                        })}
                      </p>
                    </Flex>
                  </Flex>
                  <p className='notification-message-text'>
                    {renderMessageContent(message.content)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationList;
